<template>
  <div class="federated-search-slideshow">
    <div
      v-if="!localImages || !localImages.length"
      class="federated-search-slideshow__loader"
    >
      <MoonLoader />
    </div>
    <ahc-slideshow
      :lazy-load="true"
      v-if="localImages && localImages.length"
      :looped-slides="2"
      :swipe-right="loadPrevImage"
      :swipe-left="loadNextImage"
    >
      <div
        :class="`${localImages.length > 1 ? 'slideshow' : ''} swiper-container`"
      >
        <div class="swiper-wrapper">
          <figure
            v-for="(image, index) in localImages"
            v-bind:key="index"
            :class="`slideshow__slide swiper-slide ${
              localImages.length === 1
                ? 'slideshow__slide--individual-image'
                : ''
            }`"
          >
            <div class="slideshow__slide-dynamic-image">
              <img :src="image.image_url" :alt="image.caption" />
            </div>
            <figcaption
              class="slideshow__slide-media-caption slideshow__slide-media-caption--dark"
            >
              <div
                v-if="image.caption"
                class="slideshow__slide-media-caption-text"
              >
                {{ image.caption }}
              </div>
              <div
                v-if="image.pdf_url"
                class="slideshow__slide-media-caption-text federated-search-slideshow__link"
              >
                <a :href="image.pdf_url">Download PDF</a>
              </div>
              <div
                v-if="localImages && localImages.length > 1"
                class="slideshow__slide-media-caption-counter"
              ></div>
            </figcaption>
          </figure>
        </div>

        <button
          v-if="localImages && localImages.length > 1"
          @click="this.loadPrevImage"
          class="swiper-button-prev slideshow__button-prev federated-search-slideshow__button"
        >
          <ahc-icon
            icon-name="arrow-left-circle"
            icon-name-hover="arrow-left-circle-hover"
            :focus-parent="true"
            width="72"
            height="72"
          >
          </ahc-icon>
        </button>
        <button
          v-if="localImages && localImages.length > 1"
          @click="this.loadNextImage"
          class="swiper-button-next slideshow__button-next federated-search-slideshow__button"
        >
          <ahc-icon
            icon-name="arrow-right-circle"
            icon-name-hover="arrow-right-circle-hover"
            :focus-parent="true"
            width="72"
            height="72"
          >
          </ahc-icon>
        </button>
      </div>
    </ahc-slideshow>
  </div>
</template>

<script lang="js">

import { MoonLoader } from '@saeris/vue-spinners'
import ahcSlideshow from "./../slideshow/slideshow.vue";
import ahcIcon from "./../icon/icon.vue";
import * as PDFJS from "pdfjs-dist";

export default {
  name: 'ahc-federated-search-slideshow',
  components: {
    MoonLoader,
    ahcSlideshow,
    ahcIcon
  },
  methods: {
    // Fetches the entire Pdf and loads the pages into a store
    async fetchPdfImages() {
      fetch(this.pdfUrl, {signal: this.abortController.signal})
      .then((response) => response.blob())
      .then((blob) => {

        const reader = new FileReader();

        reader.onloadend = () => {
          this.arrayBuffer = reader.result;
          this.storeImages().then(async (promises) => {
            this.imagePromises = promises;
            // We first load the first and the last image to avoid loop issues
            await this.loadImage();
            if (promises.length > 0) {
              await this.loadImage(1);
              await this.loadImage(promises.length - 1);
            }
          });
        };

        reader.onerror = () => {
          throw new Error('Failed to read the file.');
        };

        reader.readAsArrayBuffer(blob);
      })
      .catch((error) => {
        console.error(error);
      });
    },
    // Updates the index and calls lazyLoadImages to get the next images
    async loadNextImage() {
      if (!this.pdfUrl) return;
      if (this.currentIndex === this.localImages.length - 1) {
        this.currentIndex = 0;
      } else {
        this.currentIndex = this.currentIndex + 1;
      }
      await this.lazyLoadImages();
    },
    // Lazy converts the previously fetch images. Converts the image before and after to avoid 
    // blank screens when swiping
    async lazyLoadImages() {
      // Load this current index
      await this.loadImage();
      // Preload the next one up
      if (this.currentIndex > 0 && this.currentIndex !== this.localImages.length - 1) {
        await this.loadImage(this.currentIndex + 1);
      }
      if (this.currentIndex !== 0) {
        await this.loadImage(this.currentIndex - 1);
      }

      const elementsWithSlideIndexAttr = document.querySelectorAll('[data-swiper-slide-index]');

      elementsWithSlideIndexAttr.forEach(element => {
        const slideIndex = parseInt(element.getAttribute('data-swiper-slide-index'));
        const nestedCaption = element.querySelector('.slideshow__slide-media-caption');
        if (nestedCaption) {
          nestedCaption.style.display = slideIndex === this.currentIndex ? 'block' : 'none';
        }
      });
    },
    // Updates the index and calls lazyLoadImages to get the previous images
    async loadPrevImage() {
      if (!this.pdfUrl) return;
      if (this.currentIndex === 0) {
        this.currentIndex = this.localImages.length - 1;
      } else {
        this.currentIndex = this.currentIndex - 1;
      }
      await this.lazyLoadImages();
    },
    // Take an index and called convertPageToImage for the selected image and
    // add a data url to the record
    async loadImage(index = this.currentIndex) {
      const imageRecord = this.localImages[index];
      if (!imageRecord.image_url) {
        const image = await this.convertPageToImage(this.imagePromises[index]).then((result) => result);
        imageRecord.image_url = image;
      }

    },
    // Stores the rerieved images into localImages
    storeImages() {
      return new Promise((resolve, reject) => {
        PDFJS.getDocument(this.arrayBuffer).promise.then((pdf) => {
          this.totalImages = pdf.numPages;
          const promises = [];
          for (let i = 1; i <= this.totalImages; i++) {
            promises.push(
            pdf.getPage(i).then((page) => page))
            this.localImages.push({image_url: null, pdf_url: this.pdfUrl})
          }
          Promise.all(promises)
            .then((promises) => {
              resolve(promises);
            })
            .catch((error) => {
              reject(error);
            });
        });
    })
  },
  // Converts stored buffer data to a displayable blob
  convertPageToImage(page) {
      const viewport = page.getViewport({ scale: 1 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      return page.render(renderContext).promise.then(() => {
        return canvas.toDataURL();
      })
    },
  },
  props: {
    images: {
      type: Array,
      required: false,
    },
    pdfUrl: {
      type: String,
      require: false
    }
  },
  data() {
    return {
      abortController: new AbortController(),
      localImages: this.images ? this.images : [],
      totalImages: this.images ? this.images.length : null,
      imagePromises: [],
      currentIndex: 0,
    }
  },
  computed: {
  },
  beforeDestroy() {
    this.abortController.abort();
  },
  async mounted() {
    if (this.pdfUrl) await this.fetchPdfImages()
  },

}
</script>

<style lang="scss">
@import "federated-search-slideshow";
</style>
